import { useEffect, useRef, useState, type FormEvent } from "react";
import { api, type ApiResult, type AuthSuccess } from "../api.js";
import { useAuth } from "../auth.js";
import { DesktopDownloadCta } from "../DesktopDownloadCta.js";
import { HeroBrand, LocaleToggle, PasswordField, ThemeToggle, useLocale } from "../ui.js";
import type { AuthView } from "../App.js";
import type { TranslateKey } from "../i18n.js";

function feedbackFor(res: ApiResult<AuthSuccess>): TranslateKey {
  if (res.status === 0) return "loginNetworkError";
  if (res.status === 429) return "loginRateLimited";
  if (res.status === 404 || res.status === 502) return "loginProxyError";
  if (res.status >= 500) return "loginServiceUnavailable";
  if (res.failureKind === "origin_denied") return "loginOriginDenied";
  const denial = res.data?.denialCode ?? res.denialCode;
  if (denial === "two_factor_invalid") return "loginInvalidCode";
  if (denial === "pending_approval") return "pendingApproval";
  if (denial === "account_blocked") return "blocked";
  if (denial === "email_unverified") return "loginEmailUnverified";
  if (denial === "invalid_credentials") return "invalidCreds";
  if (denial === "otp_delivery_failed") return "loginMailUnavailable";
  if (res.status === 403) return "loginForbidden";
  return "loginUnexpectedResponse";
}

export function LoginPage({ onNavigate }: { onNavigate: (v: AuthView) => void }) {
  const { tr } = useLocale();
  const { setSession } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [twoFactor, setTwoFactor] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<TranslateKey | null>(null);
  const [requestId, setRequestId] = useState<string | null>(null);
  const [retryRemaining, setRetryRemaining] = useState(0);
  const inFlight = useRef(false);
  const mounted = useRef(true);
  const passwordRef = useRef<HTMLInputElement>(null);
  const codeRef = useRef<HTMLInputElement>(null);
  const errorRef = useRef<HTMLDivElement>(null);

  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => { if (error) errorRef.current?.focus(); }, [error]);
  useEffect(() => { if (twoFactor) codeRef.current?.focus(); }, [twoFactor]);
  useEffect(() => {
    if (retryRemaining <= 0) return;
    const timer = setTimeout(() => setRetryRemaining((seconds) => Math.max(0, seconds - 1)), 1000);
    return () => clearTimeout(timer);
  }, [retryRemaining]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (inFlight.current || retryRemaining > 0) return;
    inFlight.current = true;
    setBusy(true);
    setError(null);
    setRequestId(null);
    try {
      const res = await api.login(email.trim(), password, twoFactor ? code.trim() : undefined);
      if (!mounted.current) return;
      if (res.ok && res.status === 200 && res.data?.session && res.data.account) {
        setSession(res.data.account);
        return;
      }
      const denial = res.data?.denialCode ?? res.denialCode;
      // A server error must never masquerade as an MFA challenge.
      if ((res.status === 200 || res.status === 401) &&
        (res.data?.twoFactorRequired === true || denial === "two_factor_required")) {
        setTwoFactor(true);
        setError(null);
        return;
      }
      setError(feedbackFor(res));
      setRequestId(res.requestId ?? null);
      setRetryRemaining(res.retryAfterSeconds ?? 0);
    } catch {
      if (mounted.current) setError("loginNetworkError");
    } finally {
      inFlight.current = false;
      if (mounted.current) setBusy(false);
    }
  }

  function retry() {
    setError(null);
    setRequestId(null);
    setPassword("");
    setCode("");
    passwordRef.current?.focus();
  }

  return (
    <main className="ss-login">
      <HeroBrand />
      <div className="ss-login-form-wrap">
        <form className="ss-login-card" onSubmit={submit} aria-busy={busy}>
          <div className="ss-login-toprow">
            <ThemeToggle />
            <LocaleToggle />
          </div>
          <h2>{tr("login")}</h2>
          <p className="sub">{tr("loginSub")}</p>
          <div className="ss-field">
            <label htmlFor="email">{tr("email")}</label>
            <input id="email" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} disabled={busy} required />
          </div>
          <PasswordField id="password" label={tr("password")} value={password} onChange={setPassword} autoComplete="current-password" disabled={busy} inputRef={passwordRef} />
          {twoFactor ? (
            <>
              <div className="msg info">
                <strong>{tr("twoFactorTitle")}</strong>
                <div>{tr("twoFactorSub")}</div>
              </div>
              <div className="ss-field">
                <label htmlFor="twofa">{tr("code")}</label>
                <input
                  id="twofa"
                  ref={codeRef}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  required
                  disabled={busy}
                  autoFocus
                />
              </div>
            </>
          ) : null}
          {error ? (
            <div className="msg error" role="alert" tabIndex={-1} ref={errorRef}>
              <div>{tr(error)}</div>
              {requestId ? <div>{tr("loginRequestId")}: {requestId}</div> : null}
              {retryRemaining > 0 ? <div>{tr("loginRetryIn")} {retryRemaining} {tr("seconds")}</div> : null}
              <button type="button" className="ss-btn-ghost" onClick={retry} disabled={busy || retryRemaining > 0}>{tr("loginRetry")}</button>
            </div>
          ) : null}
          <button className="ss-login-submit" type="submit" disabled={busy || retryRemaining > 0}>
            {busy ? tr("loginBusy") : twoFactor ? tr("verifyLogin") : tr("login")}
          </button>
          <div className="btn-row">
            <button type="button" className="ss-btn-ghost" onClick={() => onNavigate("signup")}>
              {tr("toSignup")}
            </button>
            <button type="button" className="ss-btn-ghost" onClick={() => onNavigate("forgot")}>
              {tr("forgot")}
            </button>
          </div>
          <DesktopDownloadCta />
        </form>
      </div>
    </main>
  );
}
